import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight, Star } from "lucide-react";

const testimonials = [
  {
    name: "Dr. Adrienne Matthews",
    quote: "Meeting Elise was pure happenstance, but our chance meeting has revealed itself to be FATE! My decision to approach her and later ask her to help me improve my leg through overcoming and conquering my fear of heavy squatting has been one of the best decisions I made this year thus far. Elise is attentive, inquisitive, knowledgeable, leads by example, has a kind heart and spirit and has taken a vested interest in my fitness goals and success. She is a pure delight to work with as a person and trainer.",
  },
  {
    name: "Grace Stewart",
    quote: "When choosing a personal trainer you can tell in an instant that this woman knows what she is doing as she wears it well herself! She's helped me with both training and nutrition and she quickly got me to the next level. Elise, you're a class unto yourself and people need you. So many trainers do not teach how to avoid injury. Elise does.",
  },
  {
    name: "Kim Christianson",
    quote: "Elise Redfield is at the top of her industry. I have never come across such a dedicated and caring professional. She adapts well to all types of people and levels of athleticism. She checks in on every single one of her clients on a regular basis. Elise does not expect anyone to be perfect or an \"animal\" in the gym - she only asks for people that are dedicated to meet their goals. Elise will absolutely take you to your healthiest body ever - no matter your age or capabilities.",
  },
  {
    name: "Lynette Crandall",
    quote: "Elise is very motivating as a trainer. She cares about you as an individual and is always willing to adjust your workout to meet your needs. She uses a holistic approach that covers mind, body, exercise, nutrition, and even attitude. Working out with Elise makes you want to work harder, get stronger, and do better! She takes great pride in your progress and is a great partner on a path to a healthier, stronger you.",
  },
  {
    name: "Richelle Kern",
    quote: "There are no words that can describe how wonderful Elise is as a personal trainer. She is knowledgeable and passionate about what she does and makes your goals her main priority. I look forward to my workouts with her every week because I know she will push me to be my very best. Without her guidance, I could have never imagined that I could be where I am now. Hiring Elise will be the best decision you will ever make.",
  },
  {
    name: "Kathy Roedel",
    title: "Semi-retired Executive, 57",
    quote: "I contacted Elise and she listened to my desired goals, evaluated my starting point and then began our twice weekly sessions to help me reach them. She mixes up the workouts to keep them interesting and effective. I look forward to our sessions because Elise makes them fun, even when tough. She is an expert in fitness and nutrition. I have made some real progress. Lots of work for my tailor taking in dresses and pants because my body shape is changing!",
  },
  {
    name: "Stefanie Mulquenny",
    title: "Age 54",
    quote: "Elise Redfield Training has completely changed my life. I know at 54 years old I could not have changed my fitness and wellness if not for her personal training. Elise's focus on helping me improve my emotional, exercise/training, food choices and sleep choices have reshaped me. I am a better version of myself today because she not only taught me how to train and eat better but she believed in me! Meeting her and working with her has been life changing.",
  },
  {
    name: "Lisa Harris",
    quote: "Elise is absolutely amazing! I have worked with several trainers in the past and all have doubted my abilities because I am overweight. Elise is patient, and meets you where you are. She will push and encourage you and helps you keep yourself accountable. She is well informed and educated on an array of topics. I learned so much about my body by training with Elise. Look no further - Elise will help you accomplish your goals!",
  },
  {
    name: "Stephanie McKenna",
    quote: "Elise is probably one of my favorite people in the whole world. She is such a wonderful person on top of being an awesome trainer. I've had a lot of trainers in the past and she by far makes me feel the most comfortable and pushes me hard. I started training with her this year and I feel strong physically and mentally. She always shows up ready to rock, she's never late, and she always has an agenda so time is well spent with her. She's the real deal!",
  },
];

export const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const autoPlayRef = useRef(null);

  const nextTestimonial = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Auto-advance every 8 seconds
  useEffect(() => {
    autoPlayRef.current = setInterval(nextTestimonial, 8000);
    return () => clearInterval(autoPlayRef.current);
  }, []);

  const resetAutoPlay = () => {
    clearInterval(autoPlayRef.current);
    autoPlayRef.current = setInterval(nextTestimonial, 8000);
  };

  const handleNext = () => {
    nextTestimonial();
    resetAutoPlay();
  };

  const handlePrev = () => {
    prevTestimonial();
    resetAutoPlay();
  };

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      x: direction < 0 ? 100 : -100,
      opacity: 0,
    }),
  };

  return (
    <section id="testimonials" className="py-24 md:py-32 bg-graphite-dark" data-testid="testimonials-section">
      <div className="max-w-screen-xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="font-body text-xs tracking-[0.3em] uppercase text-sand mb-4">
            Client Stories
          </p>
          <h2 className="font-heading text-4xl md:text-5xl tracking-wide uppercase text-bone mb-4">
            Transformations
          </h2>
          <p className="font-accent text-lg italic text-bone/60 max-w-xl mx-auto">
            Real women. Real results. Real strength.
          </p>
        </motion.div>

        {/* Testimonial Carousel */}
        <div className="relative max-w-4xl mx-auto">
          {/* Quote Icon */}
          <div className="absolute -top-8 left-1/2 -translate-x-1/2">
            <Quote size={48} className="text-sand/30" fill="currentColor" />
          </div>

          {/* Testimonial Card */}
          <div className="relative min-h-[320px] md:min-h-[280px] flex items-center">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="w-full text-center px-4 md:px-12"
              >
                {/* Stars */}
                <div className="flex justify-center gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className="text-sand" fill="#D4C5B5" />
                  ))}
                </div>

                {/* Quote */}
                <blockquote className="font-body text-lg md:text-xl text-bone/90 leading-relaxed mb-8">
                  "{testimonials[currentIndex].quote}"
                </blockquote>

                {/* Author */}
                <div>
                  <p className="font-heading text-lg tracking-wider uppercase text-bone">
                    {testimonials[currentIndex].name}
                  </p>
                  {testimonials[currentIndex].title && (
                    <p className="font-body text-sm text-bone/50 mt-1">
                      {testimonials[currentIndex].title}
                    </p>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={handlePrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 p-3 text-bone/40 hover:text-bone transition-colors"
            aria-label="Previous testimonial"
            data-testid="testimonial-prev"
          >
            <ChevronLeft size={32} />
          </button>
          <button
            onClick={handleNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 p-3 text-bone/40 hover:text-bone transition-colors"
            aria-label="Next testimonial"
            data-testid="testimonial-next"
          >
            <ChevronRight size={32} />
          </button>

          {/* Dots Navigation */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setDirection(index > currentIndex ? 1 : -1);
                  setCurrentIndex(index);
                  resetAutoPlay();
                }}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? "bg-sand w-6" 
                    : "bg-bone/20 hover:bg-bone/40"
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
                data-testid={`testimonial-dot-${index}`}
              />
            ))}
          </div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-20 grid grid-cols-3 gap-8 max-w-2xl mx-auto text-center"
        >
          <div>
            <p className="font-heading text-4xl md:text-5xl text-sand mb-2">45+</p>
            <p className="font-body text-xs tracking-wider uppercase text-bone/50">Years Experience</p>
          </div>
          <div>
            <p className="font-heading text-4xl md:text-5xl text-sand mb-2">500+</p>
            <p className="font-body text-xs tracking-wider uppercase text-bone/50">Women Transformed</p>
          </div>
          <div>
            <p className="font-heading text-4xl md:text-5xl text-sand mb-2">35+</p>
            <p className="font-body text-xs tracking-wider uppercase text-bone/50">Age is Power</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
